import { Drawer } from "antd";
import React, { useEffect, useState } from "react";
import { DrawerConstants, getFormInputFields } from "./constants";
import styles from "./FilterDrawer.module.scss";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { InputfieldTypes } from "../../../enums/InputFieldAttributesEnums";
import InputField from "../../../reusable-components/TNInputField";
import TNGoogleAddressSuggestions from "../../../reusable-components/TNGoogleSearch";
import TNButton from "../../../reusable-components/TNButton";
import { ButtonType } from "../../../enums/ButtonAttributesEnums";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";
import {
  arrayToOptions,
  getFilteredOptions,
} from "../../../utils/getDropdownOptions";
import { useFormContext } from "react-hook-form";
import { useQuarryDatabaseContext } from "../../../context/QuarryDatabaseFilterContext";

interface FilterDrawerProps {
  visible?: boolean;
  onClose?: () => void;
  handleClearAllFilter?: () => void;
}

const FilterDrawer = ({
  visible,
  onClose,
  handleClearAllFilter,
}: FilterDrawerProps) => {
  const { getValues, watch, clearErrors, setError, setValue, reset } =
    useFormContext();
  const { quarryDatabaseFilters, updateQuarryDatabaseFilters } =
    useQuarryDatabaseContext();
  const [isRadiusDisabled, setRadiusDisabled] = useState<boolean>(true);

  const addressData = watch("addressData");
  const addressField = watch("addressField");
  const materialTypeField = watch("materialType");
  const categoryField = watch("category");

  const {
    WIDTH,
    TITLE,
    BUTTON: { CLEAR_FILTER_LABEL, CANCEL_LABEL, APPLY_LABEL },
    MESSAGES: { REQUIRED, NUMBERS_ONLY },
  } = DrawerConstants;
  const DrawerHeader = <span className={styles.drawerTitle}>{TITLE}</span>;
  const clearValues = () => {
    handleClearAllFilter();
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };
  const handleApply = () => {
    const inputValue = getValues("radius");
    const addressData = getValues("addressData");

    if (!isRadiusDisabled) {
      if (inputValue === "" || !inputValue) {
        setError("radius", {
          message: REQUIRED,
        });
        return;
      } else if (
        !Number.isFinite(Number(inputValue)) ||
        !Number(inputValue) > 0
      ) {
        setError("radius", {
          message: NUMBERS_ONLY,
        });
        return;
      }
    } else {
      if (!!inputValue) setValue("radius", null);
    }
    updateQuarryDatabaseFilters({
      materialType: getValues("materialType"),
      category: getValues("category"),
      address: getValues("addressField"),
      latitude: addressData?.lat,
      longitude: addressData?.lng,
      radius: inputValue || null,
    });
    onClose();
  };

  const DrawerFooter = (
    <div className={styles.drawerFooter}>
      <div className={styles.clearButton} onClick={clearValues}>
        {CLEAR_FILTER_LABEL}
      </div>
      <div className={styles.drawerButtons}>
        <TNButton
          text={CANCEL_LABEL}
          type={ButtonType.DEFAULT}
          onClick={handleCancel}
        />
        <TNButton
          text={APPLY_LABEL}
          type={ButtonType.PRIMARY}
          onClick={handleApply}
        />
      </div>
    </div>
  );
  const { FetchQuarryMaterialCategory, FetchQuarryMaterialType } =
    QuarryDatabaseService();
  const {
    data: materialCategoryData,
    refetch: refetchMaterialCategory,
    isFetching: isMaterialCategoryRefetching,
  } = FetchQuarryMaterialCategory({
    type: materialTypeField ?? [],
  });

  const {
    data: materialTypeData,
    refetch: refetchMaterialType,
    isFetching: isMaterialTypeFetching,
  } = FetchQuarryMaterialType({
    category: categoryField ?? [],
  });

  const materialCategoryOptions = arrayToOptions(
    materialCategoryData?.data ?? [],
  );
  const materialTypeOptions = arrayToOptions(materialTypeData?.data ?? []);
  const [materialTypeSearchValue, setMaterialTypeSearchValue] = useState("");
  const [materialCategorySearchValue, setMaterialCategorySearchValue] =
    useState("");
  const getOptionsList = (fieldName: string) => {
    switch (fieldName) {
      case "materialType":
        return (
          getFilteredOptions(
            materialTypeOptions ?? [],
            materialTypeSearchValue,
          ) ?? []
        );
      case "category":
        return (
          getFilteredOptions(
            materialCategoryOptions ?? [],
            materialCategorySearchValue,
          ) ?? []
        );
    }
  };
  const formInputFields = getFormInputFields({
    materialTypeSearchValue,
    materialCategorySearchValue,
    setMaterialTypeSearchValue,
    setMaterialCategorySearchValue,
  });

  const handleRadiusChange = () => {
    const inputValue = getValues("radius");

    if (Number.isFinite(Number(inputValue)) && Number(inputValue) > 0) {
      setValue("radius", Number(inputValue)?.toFixed(2));
      clearErrors("radius");
    } else if (inputValue === "" || !inputValue) {
      setError("radius", {
        message: "required",
      });
    } else {
      setError("radius", {
        message: "Only numbers allowed",
      });
    }
  };

  useEffect(() => {
    refetchMaterialCategory();
  }, [materialTypeField]);

  useEffect(() => {
    refetchMaterialType();
  }, [categoryField]);

  useEffect(() => {
    if (!!addressData && !!addressField) {
      setRadiusDisabled(false);
      handleRadiusChange();
      return;
    }
    setValue("radius", null);
    setValue("addressData", undefined);
    setRadiusDisabled(true);
    clearErrors("radius");
  }, [addressData, addressField]);

  useEffect(() => {
    if (!visible) return;
    reset({
      materialType: quarryDatabaseFilters?.materialType ?? [],
      category: quarryDatabaseFilters?.category ?? [],
      addressField: quarryDatabaseFilters?.address ?? null,
      radius: quarryDatabaseFilters?.radius ?? null,
      addressData: quarryDatabaseFilters?.latitude
        ? {
            lat: quarryDatabaseFilters?.latitude,
            lng: quarryDatabaseFilters?.longitude,
          }
        : null,
    });
  }, [visible, quarryDatabaseFilters]);

  return (
    <Drawer
      visible={visible}
      onClose={handleCancel}
      maskClosable={false}
      width={WIDTH}
      destroyOnClose
      title={DrawerHeader}
      className={styles.filter}
      footer={DrawerFooter}
    >
      {formInputFields?.map((field) => {
        const value = watch(field.name) ?? [];
        const handleChange = (newValue) => {
          setValue(field.name, newValue);
        };
        return (
          <>
            <ConditionalRender
              visible={field.type === InputfieldTypes.MULTIPLE_SELECT_SEARCH}
            >
              <InputField
                name={field.name}
                label={field.label}
                placeholder={field.placeholder}
                type={InputfieldTypes.MULTIPLE_SELECT_SEARCH}
                bordered
                values={value}
                onChange={handleChange}
                resetCheckbox={() => handleChange([])}
                setSearch={field.setSearch}
                options={getOptionsList(field.name)}
                searchValue={field.searchValue}
              />
            </ConditionalRender>
            <ConditionalRender visible={field.type === InputfieldTypes.GOOGLE}>
              <div className={styles.addressField}>
                <TNGoogleAddressSuggestions
                  isFilterField
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender visible={field.type === InputfieldTypes.NUMBER}>
              <div className={styles.addressField}>
                <InputField
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                  type={InputfieldTypes.TEXT}
                  addonAfter={field.addonAfter}
                  disabled={isRadiusDisabled}
                  onBlur={handleRadiusChange}
                />
              </div>
            </ConditionalRender>
          </>
        );
      })}
    </Drawer>
  );
};

export default FilterDrawer;
